import { cloneDeep, isEqual } from 'lodash';

// Types
type PrepareHeaderFunction = (header: any) => any;

export interface NormalizeSettingHeadersOptions {
  excludedFields?: string[];
  prepareHeader?: PrepareHeaderFunction;
}

export interface NormalizeSettingHeadersResult {
  headers: any[];
  changed: boolean;
}

export interface NormalizeSettingConfigResult {
  config: any;
  changed: boolean;
}

// Public normalizers
export function normalizeSettingHeaders(
  defaultHeaders: any[] = [],
  savedHeaders: any[] = [],
  options: NormalizeSettingHeadersOptions = {}
): NormalizeSettingHeadersResult {
  const excludedFields = new Set(options.excludedFields ?? []);
  const preparedDefaultHeaders = prepareDefaultHeaders(defaultHeaders, excludedFields, options.prepareHeader);
  const defaultHeaderByField = createHeaderMap(preparedDefaultHeaders);
  const savedFields = new Set<string>();
  const mergedSavedHeaders = mergeSavedHeaders(savedHeaders, defaultHeaderByField, excludedFields, savedFields);
  const newHeaders = getNewHeaders(preparedDefaultHeaders, savedFields);
  const headers = [...mergedSavedHeaders, ...newHeaders];

  return {
    headers,
    changed: !isEqual(toStorageComparable(headers), toStorageComparable(savedHeaders)),
  };
}

export function normalizeSettingConfig(defaultConfig: any = {}, savedConfig: any = null): NormalizeSettingConfigResult {
  const config = cloneDeep(defaultConfig ?? {});

  if (savedConfig && typeof savedConfig === 'object' && !Array.isArray(savedConfig)) {
    Object.assign(config, cloneDeep(savedConfig));
  }

  return {
    config,
    changed: !isEqual(toStorageComparable(config), toStorageComparable(savedConfig)),
  };
}

// Header merge helpers
function prepareDefaultHeaders(
  defaultHeaders: any[],
  excludedFields: Set<string>,
  prepareHeader?: PrepareHeaderFunction
): any[] {
  return toHeaderArray(defaultHeaders)
    .filter((header: any) => !excludedFields.has(header?.field))
    .map((header: any) => prepareHeader
      ? prepareHeader(cloneDeep(header))
      : cloneDeep(header)
    );
}

function createHeaderMap(headers: any[]): Map<string, any> {
  return new Map<string, any>(
    headers
      .filter((header: any) => header?.field)
      .map((header: any): [string, any] => [header.field, header])
  );
}

function mergeSavedHeaders(
  savedHeaders: any[],
  defaultHeaderByField: Map<string, any>,
  excludedFields: Set<string>,
  savedFields: Set<string>
): any[] {
  return toHeaderArray(savedHeaders)
    .filter((header: any) => isValidSavedHeader(header, defaultHeaderByField, excludedFields))
    .map((savedHeader: any) => {
      savedFields.add(savedHeader.field);
      return mergeSettingHeader(defaultHeaderByField.get(savedHeader.field), savedHeader);
    });
}

function isValidSavedHeader(
  header: any,
  defaultHeaderByField: Map<string, any>,
  excludedFields: Set<string>
): boolean {
  return !!header?.field
    && !excludedFields.has(header.field)
    && defaultHeaderByField.has(header.field);
}

function getNewHeaders(preparedDefaultHeaders: any[], savedFields: Set<string>): any[] {
  return preparedDefaultHeaders
    .filter((header: any) => !savedFields.has(header.field));
}

function mergeSettingHeader(defaultHeader: any, savedHeader: any): any {
  const normalizedHeader = {
    ...defaultHeader,
    visible: savedHeader.visible ?? defaultHeader.visible,
  };

  const hasSubHeader = savedHeader.hasSubHeader ?? defaultHeader.hasSubHeader;
  if (hasSubHeader !== undefined) {
    normalizedHeader.hasSubHeader = hasSubHeader;
  } else {
    delete normalizedHeader.hasSubHeader;
  }

  return normalizedHeader;
}

// Shared small helpers
function toHeaderArray(headers: any): any[] {
  return Array.isArray(headers) ? headers : [];
}

function toStorageComparable(value: any): any {
  const json = JSON.stringify(value);
  return json === undefined ? undefined : JSON.parse(json);
}
